/**
 * Dialog para criação de novo contato
 * 
 * Permite criar contato com nome, telefone (com seletor de código de país) e email
 * Avatar é gerenciado automaticamente pelo webhook do WhatsApp
 *
 * @version 1.3.1
 * RELEASE: 2026-08-28 v1.3.1 - Criação passa a usar a RPC
 *          find_or_create_contact_for_customer (v1.3.0) em modo estrito, no lugar
 *          do INSERT direto na tabela contacts (que falhava com
 *          "column contacts.customer_id does not exist").
 * RELEASE: 2026-08-21 v1.3.0 - Padrão visual "Nova anotação" (header premium com

 *          ícone, corpo scrollável e rodapé fixo). Correção do atalho "Cadastro
 *          completo de cliente": o modal simples agora apenas se OCULTA (sem
 *          avisar o pai), evitando que chamadores que renderizam este componente
 *          condicionalmente ({open && <Dialog/>}) o desmontem antes do
 *          CustomerFormDialog montar.
 * RELEASE: 2026-08-21 v1.2.0 - Atalho "Cadastro completo de cliente": abre o
 *          CustomerFormDialog a partir deste modal simples, devolvendo o contato
 *          principal criado pelo mesmo onSuccess (auto-seleção no chamador).
 *          Visível apenas com permissão de criação em Clientes (crm_customers).
 */


import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Button } from '@/components/ui/button';
import { Loader2, UserPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { normalizePhoneIdentifier } from '@/lib/phone-utils';
import { toast } from 'sonner';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { usePhoneDuplicateCheck } from '@/hooks/usePhoneDuplicateCheck';
import { usePermissions } from '@/hooks/usePermissions';
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';

// Schema de validação (avatar é gerenciado automaticamente pelo webhook)
const contactSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(255, 'Nome muito longo'),
  phone: z.string().trim().max(50, 'Telefone muito longo').optional().or(z.literal('')),
  email: z.string().trim().email('Email inválido').max(255, 'Email muito longo').optional().or(z.literal('')),
  seller_id: z.string().nullable().optional(),
}).refine(data => data.phone || data.email, {
  message: 'Informe pelo menos um telefone ou email',
  path: ['phone'],
});

type ContactFormData = z.infer<typeof contactSchema>;

interface CreateContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  /**
   * Callback de sucesso.
   * v1.1.0 — recebe (opcionalmente) o contato recém-criado, permitindo que o
   * chamador selecione o registro automaticamente (ex.: botão "+" do modal
   * de tarefas). Chamadores antigos podem ignorar o parâmetro.
   */
  onSuccess: (created?: { id: string; name: string; customer_id: string | null }) => void;
  /**
   * v1.2.0 — exibe o atalho "Cadastro completo de cliente".
   * Continua condicionado à permissão de criação em Clientes.
   */
  allowFullCustomer?: boolean;
}

export function CreateContactDialog({ 
  open, 
  onOpenChange, 
  tenantId, 
  onSuccess,
  allowFullCustomer = true,
}: CreateContactDialogProps) {
  const [loading, setLoading] = useState(false);
  const { checkPhoneDuplicate } = usePhoneDuplicateCheck();
  // v1.2.0 — controle do modal completo de cliente e da permissão de acesso
  const { canCreate } = usePermissions();
  const canCreateCustomer = allowFullCustomer && canCreate('crm_customers');
  const [showCustomerForm, setShowCustomerForm] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      seller_id: null,
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setLoading(true);

    // VALIDAÇÃO: Verificar duplicidade antes de inserir (usando normalização)
    if (data.phone) {
      const { isDuplicate, existingContact } = await checkPhoneDuplicate(
        tenantId,
        data.phone
      );

      if (isDuplicate) {
        toast.error(
          `Este telefone já pertence ao contato "${existingContact?.name || 'Sem nome'}"`,
          { duration: 5000 }
        );
        setLoading(false);
        return;
      }
    }
    try {
      // v1.3.1 — criação 100% via RPC (frontend não grava direto na tabela).
      // A RPC valida tenant, normaliza telefone e recusa duplicidade
      // (p_strict_create = true) devolvendo apenas o UUID do contato.
      const { data: createdId, error } = await supabase.rpc(
        'find_or_create_contact_for_customer' as any,
        {
          p_name: data.name,
          p_phone: normalizePhoneIdentifier(data.phone) || null,
          p_email: data.email || null,
          p_seller_id: data.seller_id || null,
          p_strict_create: true,
        } as any
      );

      if (error) {
        // 23505 = unique_violation levantada pela RPC no modo estrito
        if (error.code === '23505') {
          toast.error(error.message || 'Já existe um contato com este telefone');
          return;
        }
        throw error;
      }

      toast.success('Contato criado com sucesso');
      form.reset();
      onOpenChange(false);
      // Contato recém-criado nunca possui cliente vinculado ainda.
      onSuccess({ id: createdId as unknown as string, name: data.name, customer_id: null });
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Erro ao criar contato');
    } finally {
      setLoading(false);
    }
  };


  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
    }
    onOpenChange(open);
  };

  return (
    <>
      {/* v1.3.0 — enquanto o cadastro completo estiver aberto, o modal simples
          apenas se oculta (não notifica o pai), preservando a montagem deste
          componente e, com ela, o CustomerFormDialog abaixo. */}
      <Dialog open={open && !showCustomerForm} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* ===== HEADER PREMIUM (padrão ContactNoteEditorDialog) ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <UserPlus className="h-3.5 w-3.5 text-primary" />
              </div>
              Novo Contato
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Adicione um novo contato à sua base.
            </DialogDescription>
          </DialogHeader>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome do contato" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="11 99999-9999"
                      disabled={loading}
                    />
                  </FormControl>
                  <FormDescription>
                    Digite DDD + número (ex: 11 99999-9999)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="email@exemplo.com" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seller_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendedor</FormLabel>
                  <FormControl>
                    <SellerSelector
                      value={field.value}
                      onSelect={field.onChange}
                      placeholder="Selecione um vendedor..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            </div>

            {/* ===== RODAPÉ FIXO (padrão premium) ===== */}
            <DialogFooter className="gap-2 sm:justify-between border-t bg-muted/30 px-6 py-3">
              {/* v1.2.0 — atalho para o cadastro completo de cliente */}
              {canCreateCustomer ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="gap-1.5"
                  disabled={loading}
                  onClick={() => {
                    // v1.3.0 — apenas oculta o modal simples e abre o completo
                    form.reset();
                    setShowCustomerForm(true);
                  }}
                >
                  <UserPlus className="h-4 w-4" />
                  Cadastro completo de cliente
                </Button>
              ) : (
                <span />
              )}

              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => handleOpenChange(false)}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Criar Contato
                </Button>
              </div>
            </DialogFooter>

          </form>
        </Form>
      </DialogContent>
      </Dialog>

      {/* v1.2.0 — cadastro completo de cliente (fora do Dialog simples para
          não herdar seu ciclo de vida; o empilhamento é resolvido pelo
          DialogDepthContext). Devolve o contato principal ao chamador. */}
      {canCreateCustomer && (
        <CustomerFormDialog
          customer={null}
          open={showCustomerForm}
          onOpenChange={(next) => {
            setShowCustomerForm(next);
            // v1.3.0 — ao fechar o cadastro completo, encerra também o fluxo
            // do modal simples (que estava apenas oculto).
            if (!next) onOpenChange(false);
          }}
          onSuccess={(newCustomer) => {
            setShowCustomerForm(false);
            onOpenChange(false);
            if (newCustomer?.main_contact_id) {
              onSuccess({
                id: newCustomer.main_contact_id,
                name: newCustomer.nome,
                customer_id: newCustomer.id,
              });
            } else {
              onSuccess();
            }
          }}
        />
      )}

    </>
  );
}
